/**
 * instrument.ts — which of the two insurance instruments a premium year is recorded on.
 *
 * ONE `doc_type`, TWO INSTRUMENTS, THREE PUBLISHED PAGES [D-B45, D-B57]. A year the driver
 * borrowed for is an **Insurance Premium Advance** (`template_key` `insurance_loan`); a year the
 * driver paid for in full is an **Insurance Premium Payment Record** (`template_key`
 * `insurance_payment`) — a self-contained document that advances nothing, charges nothing and
 * leaves nothing owed. All of them hang off `agreement_doc_type = 'INSURANCE_LOAN'` and the same
 * `insurance_loans` subject, so the arc column, the bucket, the void-and-reissue rails and every
 * census stay exactly as they are.
 *
 * AND THE TYPE BELOW STAYS TWO-VALUED, WHICH IS WHY THE REGISTRY'S THIRD KEY IS NOT A THIRD
 * INSTRUMENT HERE [D-B57]. An advance with no weekly recovery, on a contract whose hire purchase
 * agreement leaves no room for one, is published on `insurance_loan_at_end` — a page that states
 * one repayment arrangement where the ordinary Advance states another, under the SAME title. This
 * file answers what the screen SAYS, and the screen says "Insurance Premium Advance" over both: a
 * third value would ripple the noun and the heading through the board, the hazards, the rail and
 * the ledgers for a name that does not change. Which page the server picks is the server's.
 *
 * THE SERVER DERIVES THE INSTRUMENT, AND THIS FILE CANNOT. `agreement_create` picks the template
 * by key from the loan's own rows at the moment of generation, and `agreement_sign` re-asserts the
 * same shape on both arms (`AGREEMENT_INSTRUMENT_MISMATCH`) so a deposit landing between the two
 * cannot leave a false clause 1.1 on a sealed page. What `instrumentFor` below decides is
 * therefore only what the screen SAYS — the name on an empty slot, the noun in a hazard sentence,
 * the instrument the send confirmation promises — and it can never produce a wrong document.
 * A disagreement between this predicate and the server is answered by the server, out loud: the
 * mismatch refusal names both shapes and the fix is void-and-reissue.
 *
 * THE TWO LIMBS ARE THE SERVER'S, VERBATIM. Deposits covering the amount within half a pesewa
 * AND no instalment row. There is deliberately NO rate limb: the deposit is what makes the page
 * true, and a stale rate on a fully-deposited loan accrues nothing (all 12 production rows of this
 * shape qualify, 2 of them carrying a non-zero rate). The `NOT EXISTS instalments` limb is what
 * stops a financed advance later paid down through the deposit toggle from being mislabelled.
 *
 * AND `amount > 0` IS PART OF THAT VERBATIM RATHER THAN A THIRD LIMB. The server writes the
 * deposit limb as `COALESCE(SUM(deposits) >= il.amount - 0.005, false)`, and a loan with NO
 * deposit rows sums to SQL NULL: the comparison is NULL, the COALESCE makes it false, and a
 * premium of zero or less therefore answers ADVANCE server-side. No caller here can reproduce
 * that, because every one of them arrives with a deposit total already COALESCEd to 0 — and
 * `0 >= 0 - 0.005` is true. The guard below is what makes the two agree on that shape; without
 * it this predicate answered PAYMENT_RECORD where the server answers ADVANCE. Nothing live
 * reaches it (both write paths refuse a non-positive premium), so it is robustness, not repair.
 *
 * PURE, AND IMPORT-FREE. `hazards.ts` and `boardSlots.ts` both read it, and `hazards.ts` keeps
 * itself out of the component graph so vitest can run it under node — a module with no imports of
 * its own can be pulled into either without dragging anything behind it.
 */

export type InsuranceInstrument = 'ADVANCE' | 'PAYMENT_RECORD';

/**
 * The published `agreement_templates.title` of each, verbatim.
 *
 * ONE STRING EVERYWHERE — the H1, the registry row, the cover headline and every label this app
 * prints. A generated document carries its own title and that one always wins; these are what an
 * EMPTY slot is named by, and they have to be the same words the document will arrive with or the
 * board renames the row the moment it is generated.
 */
export const INSTRUMENT_TITLES: Record<InsuranceInstrument, string> = {
    ADVANCE: 'Insurance Premium Advance',
    PAYMENT_RECORD: 'Insurance Premium Payment Record',
};

/**
 * The same two names lower-cased for mid-sentence use — a hazard confirm, a consequence line.
 *
 * `hazards.ts` imports this under an alias, because the map it keys by `doc_type` carries the same
 * name for the same reason: an admin reading a sentence has the document open beside it, and a
 * noun that named it something else would read as a second instrument.
 */
export const INSTRUMENT_NOUNS: Record<InsuranceInstrument, string> = {
    ADVANCE: 'insurance premium advance',
    PAYMENT_RECORD: 'insurance premium payment record',
};

/**
 * Which `agreement_templates.template_key` each instrument is published under.
 *
 * BY KEY, NEVER BY VERSION. `uq_agreement_templates_published` is per template_key, so the two
 * families each carry their own published row and `agreement_create` selects between them by key —
 * a selector keyed on version would break the first time either family is re-cut.
 */
export const TEMPLATE_KEY_BY_INSTRUMENT: Record<InsuranceInstrument, string> = {
    ADVANCE: 'insurance_loan',
    PAYMENT_RECORD: 'insurance_payment',
};

/**
 * The third published page's `template_key` — A PAGE, AND STILL NOT A THIRD INSTRUMENT [D-B57].
 *
 * IT IS DELIBERATELY NOT A MEMBER OF THE MAP ABOVE, because that map is keyed by instrument and
 * this is the SECOND page the ADVANCE is published on: an advance with no weekly recovery of its
 * own, on a contract whose hire purchase agreement leaves no room for one, states one repayment
 * arrangement where the ordinary Advance states another under the same title. The docblock at the
 * top of this file is why the type stays two-valued; this constant is what lets the two surfaces
 * that have to name the PAGE — the board's `expectedTemplateKey` and the row's page hint — spell
 * `agreement_create`'s third branch without inventing an instrument to hang it on.
 */
export const INSURANCE_AT_END_TEMPLATE_KEY = 'insurance_loan_at_end';

/**
 * How each of the three insurance pages is named where a sentence says what a document WAS
 * generated as — `agreement_sign`'s own words, verbatim.
 *
 * TWO MAPS, BECAUSE THE SERVER KEEPS TWO. The mismatch refusal `agreement_sign` raises when a
 * document's pinned page no longer matches its subject (`AGREEMENT_INSTRUMENT_MISMATCH`) names the
 * PINNED page in full and the page now CALLED FOR in a shorter form — `INSURANCE_PAGE_CALLED_FOR`
 * below — and that second vocabulary is not decoration. Both advances are published under the title
 * "Insurance Premium Advance", so a sentence built from one map would print the same words twice in
 * the reverse direction — an at-end document on a loan that has since been given a schedule — and
 * say nothing. The server solved that collision by shortening the second half rather than by
 * inventing a clause for the first, and an admin who meets the board's hint today and the server's
 * refusal at the next signature has to be reading one sentence in two places.
 *
 * AND NEITHER MAP SAYS "PAPER". The predicate behind the at-end page is how the SERVER tells the
 * two advances apart; what the driver signs states the repayment arrangement and nothing about how
 * the agreement behind it was executed — so a hint that leaked the derivation would be naming a
 * fact the document itself is careful not to.
 *
 * AND THE TYPE ADMITS A MISS ON PURPOSE. A fourth page published on this doc_type would reach a
 * bundle that has never heard of it, and the caller falls back to `agreement_templates.title`. That
 * fallback is the document's own name and so is never WRONG — but it is not always useful: the two
 * advances carry one title between them, so a sentence that fell back on both sides would name the
 * same string twice. It degrades to a bare title, never to a false one.
 */
export const INSURANCE_PAGE_DESCRIPTIONS: Readonly<Partial<Record<string, string>>> = {
    insurance_payment: 'an Insurance Premium Payment Record',
    insurance_loan: 'an Insurance Premium Advance',
    [INSURANCE_AT_END_TEMPLATE_KEY]:
        'an Insurance Premium Advance settled at the end of the contract',
};

/**
 * The same three pages in the shorter form the second half of the sentence takes — also verbatim.
 *
 * IT IS THE MAP THAT MAKES THE SENTENCE SAY SOMETHING. "generated as an Insurance Premium Advance
 * settled at the end of the contract, and the insurance premium behind it now calls for the
 * Advance" reads as one fact; the same sentence with the long form on both sides names the two
 * advances identically. See `INSURANCE_PAGE_DESCRIPTIONS` above for why the server is followed here
 * rather than paraphrased.
 */
export const INSURANCE_PAGE_CALLED_FOR: Readonly<Partial<Record<string, string>>> = {
    insurance_payment: 'the Record',
    insurance_loan: 'the Advance',
    [INSURANCE_AT_END_TEMPLATE_KEY]: 'the Advance settled at the end of the contract',
};

/**
 * The dust guard on "the deposits cover the premium" — half a pesewa, the server's own figure.
 *
 * Two `numeric` sums compared for equality is how a premium settled to the last pesewa reads as
 * 0.004 short and prints clause 1.1's "The Borrower did not pay the whole of it" over a driver who
 * did. The tolerance is on the COMPARISON and nothing is rounded: rounding belongs at display.
 */
export const FULL_DEPOSIT_TOLERANCE = 0.005;

/** The loan's two limbs, as any surface holding an `insurance_loans` row can supply them. */
export interface InsuranceLoanShape {
    /** `insurance_loans.amount` — the premium the year was written over. */
    amount: number | null | undefined;
    /** Σ of the transactions behind this loan's `insurance_loan_deposits`. */
    depositTotal: number | null | undefined;
    /** How many `insurance_loan_instalments` rows the loan carries. */
    instalmentCount: number | null | undefined;
}

/**
 * Which instrument this loan's year would be recorded on.
 *
 * EVERY UNKNOWN ANSWERS `ADVANCE`, and that asymmetry is deliberate. An advance is what this app
 * has always called an insurance loan, so a wrong `ADVANCE` is the status quo and costs a label; a
 * wrong `PAYMENT_RECORD` is a NEW claim — that the driver paid the whole premium — printed over a
 * subject nobody has established that about. So a missing amount, an unread instalment count and
 * an absent deposit total all fall to the advance rather than guessing.
 *
 * A NULL AMOUNT IS THE SERVER'S ANSWER TOO, reached the same way: `SUM(deposits) >= NULL - 0.005`
 * is NULL, which is not TRUE, which is the selector's `else`.
 */
export function instrumentFor({
    amount,
    depositTotal,
    instalmentCount,
}: InsuranceLoanShape): InsuranceInstrument {
    if (amount === null || amount === undefined) return 'ADVANCE';
    // A premium of zero or less is the shape where a COALESCEd-to-0 deposit total and the
    // server's NULL sum part company — see the docblock. The server says ADVANCE; so does this.
    if (!(amount > 0)) return 'ADVANCE';
    // `NOT EXISTS instalments`, client-side. Anything but a counted zero — a null, an undefined,
    // a count nobody read — is an unknown, and an unknown is an advance.
    if (instalmentCount !== 0) return 'ADVANCE';
    return (depositTotal ?? 0) >= amount - FULL_DEPOSIT_TOLERANCE ? 'PAYMENT_RECORD' : 'ADVANCE';
}
